// 常见问题目录 —— 按"R语言 + 心理与行为统计学"的知识脉络组织
//
// 编排原则：
// 1. 前半部分为本科基础课脉络（R基础 → 数据处理 → 描述统计 → 概率与分布 →
//    估计与假设检验 → 常用检验方法），后半部分为研究生与科研实战常用的进阶专题
//    （多层模型、SEM、纵向数据、IRT、贝叶斯、元分析、反应时与信号检测论等）
// 2. 每个专题的问题刻意混合四种类型，避免学生只拿到"代码配方"：
//    - 概念型（这是什么、为什么）
//    - 操作型（在R中怎么做）
//    - 解读型（输出结果怎么看）
//    - 选择/陷阱型（什么时候用哪个、常见误解）

export interface FaqCategory {
  title: string;
  questions: string[];
}

export const FAQ_CATEGORIES: FaqCategory[] = [
  {
    title: 'R语言入门',
    questions: [
      'R和RStudio有什么区别？',
      '如何安装和加载R包？',
      '如何查看一个函数的帮助文档和用法示例？',
      '工作目录是什么？如何设置和查看？',
      '<- 和 = 在赋值上有什么区别？',
      '如何查看和清除当前环境中的变量？',
      '如何看懂R的常见报错信息？',
      '写R脚本有哪些好的代码习惯？',
      '如何设置随机种子让结果可重复？',
      '如何管理项目路径与R项目(.Rproj)？'
    ]
  },
  {
    title: '数据结构',
    questions: [
      '向量是什么？如何创建向量？',
      'R中有哪些基本数据类型？',
      '因子(factor)是什么？什么时候必须使用？',
      '数据框(data frame)是什么？和矩阵有什么区别？',
      '列表(list)和向量有什么区别？',
      '如何查看数据的结构和变量类型？',
      '如何用索引取出某一行、某一列或某个元素？',
      '什么是向量化运算？为什么比循环更好？',
      '什么是循环补齐(recycling)？会带来什么风险？',
      'NA、NULL、NaN、Inf有什么区别？',
      '因子水平的顺序如何设定？为什么会影响分析结果？'
    ]
  },
  {
    title: '数据导入与清洗',
    questions: [
      '如何读取CSV文件？',
      '如何读取Excel或SPSS格式的数据？',
      '数据读入后应该先做哪些检查？',
      '如何检查和处理缺失值(NA)？',
      '缺失值该删除还是插补？如何选择？',
      '如何识别和处理异常值？',
      '如何对变量进行重新编码？',
      '量表的反向计分题如何处理？',
      '如何在字符型、因子和数值型之间转换？',
      '读入的中文变成乱码怎么办？',
      '如何核对问卷数据中的逻辑错误和重复作答？'
    ]
  },
  {
    title: '数据整理与变换',
    questions: [
      '如何筛选出满足条件的数据子集？',
      '如何新增、修改和删除变量？',
      '如何按某个变量对数据排序？',
      '如何按共同的ID合并两个数据框？',
      '宽数据和长数据有什么区别？如何相互转换？',
      '如何按分组计算统计量？',
      '管道操作符(%>% 或 |>)是什么？怎么用？',
      'apply家族函数分别用在什么场合？',
      '如何对多个变量批量做同样的处理？',
      '如何把连续变量切分成分组变量？',
      '如何编写自定义函数来复用分析流程？'
    ]
  },
  {
    title: '描述统计',
    questions: [
      '均值、中位数和众数分别适合什么数据？',
      '什么时候应该用中位数而不是均值？',
      '方差和标准差到底在描述什么？',
      '为什么样本方差要除以n-1？',
      '四分位数和百分位数怎么理解？',
      '偏度和峰度说明了数据的什么特征？',
      '什么是标准分(z分数)？有什么用？',
      '如何一次得到一份完整的描述统计表？',
      '如何按组别比较描述统计结果？',
      '如何制作频数表和交叉表？',
      '论文中描述统计部分应该报告哪些指标？'
    ]
  },
  {
    title: '数据可视化',
    questions: [
      '如何绘制直方图？组距该怎么选？',
      '如何绘制箱线图？从箱线图能看出什么？',
      '如何绘制散点图并添加趋势线？',
      '如何绘制带误差棒的分组柱状图？',
      '误差棒应该画标准差、标准误还是置信区间？',
      'ggplot2的图层语法是怎么回事？',
      '如何修改图的标题、坐标轴和图例？',
      '如何用QQ图判断数据是否近似正态？',
      '如何画出符合APA格式的图？',
      '如何把图导出为高分辨率图片？',
      '如何把多张图排列在一起？'
    ]
  },
  {
    title: '概率与概率分布',
    questions: [
      '概率的基本运算规则有哪些？',
      '条件概率和独立性是什么意思？',
      '什么是随机变量？离散型和连续型有何区别？',
      '二项分布适用于什么情境？',
      '正态分布有哪些重要性质？',
      '如何用R计算正态分布的概率和分位数？',
      '什么是抽样分布？它和总体分布有什么不同？',
      '中心极限定理说了什么？为什么这么重要？',
      't分布和标准正态分布有什么区别？',
      '卡方分布和F分布分别用在哪里？',
      '泊松分布在心理学研究中什么时候会用到？'
    ]
  },
  {
    title: '抽样与参数估计',
    questions: [
      '总体、样本和抽样误差是什么关系？',
      '标准误和标准差有什么区别？',
      '什么是置信区间？"95%置信"到底指什么？',
      '如何在R中计算均值的置信区间？',
      '置信区间和假设检验有什么联系？',
      '什么是统计功效(power)？受哪些因素影响？',
      '如何进行样本量估算？',
      '为什么说效应量比p值更值得关注？',
      'Bootstrap是什么？什么时候需要用？',
      '不同抽样方法(随机、分层、整群)有何差异？'
    ]
  },
  {
    title: '假设检验基础',
    questions: [
      '原假设和备择假设分别是什么？如何提出？',
      'p值到底是什么意思？有哪些常见误解？',
      '为什么p>0.05不能说明两组没有差异？',
      '显著性水平α应该如何选择？',
      '第一类错误和第二类错误分别指什么？',
      '单尾检验和双尾检验该如何选择？',
      '统计显著和实际意义有什么区别？',
      '为什么多重比较会让结论变得不可靠？',
      '常见的多重比较校正方法如何选择？',
      '什么是FDR校正？和Bonferroni有何不同？',
      '什么是等价性检验(TOST)？如何论证"无差异"？',
      '什么是p-hacking？如何避免？',
      '完整的假设检验一般包含哪些步骤？'
    ]
  },
  {
    title: '均值比较与t检验',
    questions: [
      '单样本t检验用在什么情况？',
      '如何在R中进行独立样本t检验？',
      '配对样本t检验和独立样本t检验有什么区别？',
      't检验有哪些前提假设？',
      '如何检验数据的正态性？',
      '如何检验两组的方差是否齐性？',
      '方差不齐时应该怎么办(Welch检验)？',
      '如何计算和解读Cohen\'s d？',
      '配对设计的效应量该怎么算？',
      '如何完整解读t检验的输出结果？',
      't检验的结果按APA格式该怎么写？'
    ]
  },
  {
    title: '方差分析',
    questions: [
      '为什么不能用多次t检验代替方差分析？',
      '方差分析的基本原理是什么？',
      '如何在R中进行单因素方差分析？',
      'F值是怎么算出来的？它表示什么？',
      '方差分析显著之后还需要做什么？',
      '常见的事后检验方法该如何选择？',
      '什么是计划比较和正交对比？',
      '双因素方差分析中的主效应是什么？',
      '什么是交互作用？该如何解读？',
      '如何绘制交互作用图并做简单效应分析？',
      '重复测量方差分析适用于什么实验设计？',
      '什么是球形假设？违反了该怎么办？',
      '样本量不等时Type I/II/III平方和该怎么选？',
      '协方差分析(ANCOVA)的前提假设是什么？',
      '如何计算和解读eta方与偏eta方？'
    ]
  },
  {
    title: '相关与回归',
    questions: [
      '相关系数r表示什么？大小如何解释？',
      'Pearson相关和Spearman相关如何选择？',
      '如何检验相关系数是否显著？',
      '如何绘制相关矩阵图？',
      '"相关不等于因果"具体是什么意思？',
      '什么是偏相关和半偏相关？',
      '如何比较两个相关系数是否有差异？',
      '简单线性回归的基本原理是什么？',
      '如何解读回归系数和截距？',
      '标准化回归系数(β)该怎么理解？',
      'R方和调整R方分别表示什么？',
      '多元回归中如何解释每个自变量的作用？',
      '什么是分层回归？如何比较模型的增量解释力？',
      '回归分析有哪些前提假设？如何做残差诊断？',
      '什么是多重共线性？如何检测和处理？',
      '如何识别强影响点(杠杆值与Cook距离)？',
      '逻辑回归适用于什么因变量？如何解读OR值？',
      '如何处理计数型因变量(泊松与负二项回归)？'
    ]
  },
  {
    title: '分类数据分析',
    questions: [
      '卡方拟合优度检验用在什么情况？',
      '如何进行卡方独立性检验？',
      '如何解读卡方检验的输出结果？',
      '卡方检验有哪些前提条件？',
      '期望频数太小时该怎么办(Fisher精确检验)？',
      '配对的分类数据如何分析(McNemar检验)？',
      '分类数据的效应量如何计算？',
      '如何制作列联表并计算百分比？',
      '多向列联表如何做分层分析？'
    ]
  },
  {
    title: '非参数检验',
    questions: [
      '什么情况下应该改用非参数检验？',
      '非参数检验的优点和代价分别是什么？',
      '如何进行Mann-Whitney U检验？',
      'Wilcoxon符号秩检验适用于什么设计？',
      '如何进行Kruskal-Wallis检验？',
      'Friedman检验用在什么情况？',
      '非参数检验显著后如何做多重比较？',
      '非参数检验的结果如何解读和报告？',
      '非参数检验的效应量怎么计算？'
    ]
  },
  {
    title: '心理测量与量表分析',
    questions: [
      '信度和效度分别指什么？',
      '如何计算Cronbach\'s α？多大才可接受？',
      'α系数有哪些局限？什么时候该用McDonald\'s ω？',
      '如何进行项目分析(题总相关)？',
      '如何计算量表总分和各维度得分？',
      '探索性因素分析的基本步骤是什么？',
      '如何判断应该保留几个因子？',
      '因子旋转是什么？正交与斜交如何选择？',
      '主成分分析和因素分析有什么区别？',
      '什么是聚合效度和区分效度？如何检验？',
      '评分者一致性如何计算(kappa与ICC)？'
    ]
  },
  {
    title: '实验设计与统计功效',
    questions: [
      '被试间设计和被试内设计如何选择？',
      '什么是拉丁方设计与平衡抵消？',
      '如何进行随机分配与分层随机化？',
      '什么是混淆变量？如何在设计上控制？',
      '事前功效分析和事后功效分析有什么区别？',
      '如何在R中做各类检验的功效分析？',
      '为什么检验交互作用需要更大的样本量？',
      '什么是最小可检测效应量？如何确定？',
      '预注册中应该写清楚哪些统计细节？',
      '序贯分析和期中分析是怎么回事？'
    ]
  },
  {
    title: '中介、调节与条件过程分析',
    questions: [
      '什么是中介效应？和调节效应有何区别？',
      'Baron & Kenny逐步法有哪些局限？',
      '为什么现在推荐用Bootstrap检验间接效应？',
      '如何在R中做简单中介分析？',
      '如何解读间接效应的置信区间？',
      '如何在回归中检验调节效应？',
      '交互项为什么建议先中心化？',
      '什么是简单斜率分析和Johnson-Neyman法？',
      '什么是有调节的中介(条件过程模型)？',
      '多重中介和链式中介如何设定？',
      '横断面数据做中介分析有什么风险？'
    ]
  },
  {
    title: '多层线性模型与混合效应',
    questions: [
      '什么情况下必须使用多层模型？',
      '什么是组内相关系数(ICC)？如何解读？',
      '固定效应和随机效应有什么区别？',
      '随机截距和随机斜率分别代表什么？',
      '如何在R中拟合混合效应模型？',
      '多层模型中的组均值中心化该怎么做？',
      '混合模型的自由度和p值为什么有争议？',
      '如何比较嵌套的多层模型？',
      '重复测量为什么可以用混合模型替代方差分析？',
      '模型不收敛或奇异拟合时怎么办？',
      '多层模型的效应量和方差解释率如何报告？'
    ]
  },
  {
    title: '结构方程模型与CFA',
    questions: [
      '结构方程模型能解决什么问题？',
      '潜变量和观测变量有什么区别？',
      '如何用lavaan设定一个测量模型？',
      '模型识别(identification)是什么意思？',
      '常用的拟合指标有哪些？判断标准是多少？',
      '修正指数该如何谨慎使用？',
      '什么是测量不变性？如何逐步检验？',
      '路径分析和完整SEM有什么区别？',
      '如何在SEM框架下做潜变量中介分析？',
      'SEM对样本量有什么要求？',
      '什么是二阶因子模型和双因子模型？'
    ]
  },
  {
    title: '纵向数据与增长模型',
    questions: [
      '纵向数据有哪些常见分析思路？',
      '什么是潜变量增长模型？',
      '如何设定线性与非线性增长轨迹？',
      '时间变量应该如何编码？',
      '如何处理纵向研究中的被试流失？',
      '什么是交叉滞后模型？',
      '交叉滞后模型能否推断因果？',
      '随机截距交叉滞后模型(RI-CLPM)解决了什么问题？',
      '如何在增长模型中纳入预测变量？',
      '纵向测量不变性为什么必须检验？'
    ]
  },
  {
    title: '项目反应理论与高级测量',
    questions: [
      '项目反应理论和经典测验理论有何区别？',
      '项目难度和区分度参数分别表示什么？',
      '单参数、双参数和三参数模型有何不同？',
      '什么是项目特征曲线？如何解读？',
      '测验信息函数说明了什么？',
      '如何在R中拟合IRT模型？',
      '什么是项目功能差异(DIF)？如何检验？',
      '等级反应模型适用于什么量表？',
      '计算机自适应测验的基本原理是什么？',
      'IRT分析对样本量有什么要求？'
    ]
  },
  {
    title: '贝叶斯统计',
    questions: [
      '贝叶斯统计和频率派的核心区别是什么？',
      '先验、似然和后验分别指什么？',
      '贝叶斯因子如何解读？常用的判断标准是什么？',
      '贝叶斯因子相比p值有什么优势？',
      '先验该如何选择？会不会太主观？',
      '可信区间和置信区间有什么不同？',
      '如何在R中做贝叶斯t检验？',
      '如何用brms拟合贝叶斯回归模型？',
      '如何检查MCMC是否收敛？',
      '为什么说贝叶斯方法可以支持"没有差异"的结论？'
    ]
  },
  {
    title: '稳健统计与重抽样',
    questions: [
      '什么是稳健统计？什么时候需要用？',
      '修剪均值和M估计是什么？',
      '异常值对均值和回归的影响有多大？',
      'Bootstrap的基本步骤是什么？',
      'Bootstrap置信区间有哪几种？如何选择？',
      '什么是置换检验？适用于什么情况？',
      '置换检验和Bootstrap有什么区别？',
      '稳健回归有哪些常用方法？',
      '如何用稳健方法做组间比较？',
      '数据转换(对数、平方根)什么时候合适？'
    ]
  },
  {
    title: '缺失数据与数据质量',
    questions: [
      '缺失机制MCAR、MAR、MNAR有什么区别？',
      '如何判断数据的缺失机制？',
      '为什么不推荐整行删除或均值替代？',
      '什么是多重插补？基本流程是怎样的？',
      '如何在R中用mice做多重插补？',
      '插补后的结果如何按Rubin法则合并？',
      '什么是FIML？和多重插补如何选择？',
      '量表中个别题目缺失该如何处理？',
      '如何识别无效作答与敷衍作答？',
      '数据清洗过程应该如何记录和报告？'
    ]
  },
  {
    title: '反应时与行为实验数据',
    questions: [
      '反应时数据为什么通常呈右偏分布？',
      '反应时的极端值应该如何剔除？',
      '常见剔除标准(2.5SD、绝对阈限)如何选择？',
      '反应时应该报告均值还是中位数？',
      '对数转换会如何影响结果解释？',
      '什么是ex-Gaussian分布？参数含义是什么？',
      '如何分析速度—准确性权衡？',
      '为什么试次级数据适合用混合模型分析？',
      '如何处理练习效应和疲劳效应？',
      '如何把试次数据正确汇总到被试水平？'
    ]
  },
  {
    title: '信号检测论与心理物理',
    questions: [
      '信号检测论要解决什么问题？',
      '击中、虚报、漏报和正确拒绝分别是什么？',
      'd\'表示什么？如何计算？',
      '反应标准c该如何理解？',
      '击中率为0或1时如何做校正？',
      '什么时候使用非参数指标A\'？',
      '如何在R中批量计算信号检测论指标？',
      'ROC曲线在心理学研究中如何使用？',
      '什么是心理物理函数与阈限估计？',
      '二择一强迫选择(2AFC)数据如何分析？'
    ]
  },
  {
    title: '元分析与研究综合',
    questions: [
      '元分析要回答什么问题？',
      '不同研究的效应量如何统一换算？',
      '固定效应模型和随机效应模型如何选择？',
      '什么是异质性？I²该如何解读？',
      '如何做亚组分析和元回归？',
      '森林图应该怎么读？',
      '什么是发表偏倚？有什么后果？',
      '漏斗图和Egger检验如何使用？',
      '如何在R中用metafor做元分析？',
      '元分析的纳入排除标准该如何制定？'
    ]
  },
  {
    title: '机器学习与预测建模',
    questions: [
      '解释性建模和预测性建模有什么区别？',
      '什么是过拟合？如何避免？',
      '训练集、验证集和测试集如何划分？',
      '什么是交叉验证？常见做法有哪些？',
      '正则化(岭回归、LASSO)解决什么问题？',
      '分类模型有哪些评价指标？',
      'ROC曲线和AUC如何解读？',
      '决策树和随机森林的基本思想是什么？',
      '聚类分析有哪些常用方法？',
      '如何确定聚类的个数？',
      '机器学习结果在心理学论文中如何报告？'
    ]
  },
  {
    title: '高级可视化与结果呈现',
    questions: [
      '如何绘制原始数据点与分布的组合图？',
      '什么是雨云图(raincloud plot)？如何绘制？',
      '如何可视化交互作用与简单效应？',
      '如何绘制森林图与效应量图？',
      '如何用分面(facet)展示多组数据？',
      '如何绘制回归诊断图？',
      '如何可视化缺失数据的模式？',
      '如何制作可发表级别的图表主题？',
      '配色需要注意哪些色觉无障碍问题？',
      '如何制作交互式图表？'
    ]
  },
  {
    title: '结果报告与可重复研究',
    questions: [
      '统计结果按APA格式应该怎么写？',
      '论文的结果部分需要报告哪些统计量？',
      '数字保留几位小数？p值应该怎么写？',
      '如何制作规范的三线表？',
      '如何用R Markdown生成可重复的分析报告？',
      '常见的统计报告错误有哪些？',
      '如何检查自己的分析流程是否正确？',
      '数据和分析代码应该如何整理与共享？',
      '如何撰写清晰的数据说明文档(codebook)？'
    ]
  }
];

export const FAQ_TOTAL_QUESTIONS = FAQ_CATEGORIES.reduce(
  (sum, category) => sum + category.questions.length,
  0
);
